import { Service, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DoorMessageDataService } from '@data-services/doors';
import { DataContext } from '@services/data-context';
import { createPageResource, resolveMappedName, toNameMap } from '@utils';
import { ClientCompaniesService } from './client-companies.service';

interface ProjectParams {
  projectId: string;
}

@Service({ autoProvided: false })
export class MessageNamesService {
  private readonly companiesService = inject(ClientCompaniesService);
  private readonly dataContext = inject(DataContext);
  private readonly doorMessageDataService = inject(DoorMessageDataService);
  private readonly translate = inject(TranslateService);

  private projectParams(): ProjectParams | undefined {
    const projectId = this.dataContext.projectContext()?.id;
    return projectId ? { projectId } : undefined;
  }

  readonly userNamesResource = createPageResource<Map<string, string>, ProjectParams>({
    params: () => this.projectParams(),
    loadAsync: async ({ projectId }) => toNameMap(await this.doorMessageDataService.getMessageUserNamesAsync(projectId)),
    errorMessage: () => this.translate.instant('general.loadMessageUserNamesError'),
    fallbackValue: new Map()
  });

  readonly isLoading = computed(() => this.userNamesResource.isLoading() || this.companiesService.namesResource.isLoading());

  userName(userId: string | undefined): string {
    return resolveMappedName(this.userNamesResource.value(), userId);
  }

  companyName(companyId: string | undefined): string {
    return this.companiesService.companyName(companyId);
  }

  reloadUserNames(): void {
    if (!this.userNamesResource.isLoading()) {
      this.userNamesResource.reload();
    }
  }
}
